import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Store, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import EbayConnectPanel from '@/components/admin/ebay/EbayConnectPanel';
import EbaySyncPanel from '@/components/admin/ebay/EbaySyncPanel';
import EbaySellerOverview from '@/components/admin/ebay/EbaySellerOverview';
import EbaySalesReconciliationPanel from '@/components/admin/ebay/EbaySalesReconciliationPanel';
import EbaySageRoutingCard from '@/components/admin/ebay/EbaySageRoutingCard';

export default function AdminEbay() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadSettings = useCallback(async () => {
    try {
      const list = await base44.entities.EbaySettings.filter({ setting_key: 'global' }, '-updated_date', 1);
      setSettings(list?.[0] || { connected: false });
    } catch (_) {
      setSettings({ connected: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto w-full">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
        </Link>
        <div className="flex items-center gap-3 mb-8">
          <span className="w-10 h-10 bg-primary/15 border border-primary/30 flex items-center justify-center">
            <Store className="w-5 h-5 text-primary" />
          </span>
          <div>
            <p className="text-primary text-xs font-bold tracking-widest mb-1">SYSTEM &amp; INTEGRATIONS</p>
            <h1 className="text-xl font-bold text-white font-display">eBay Store Sync</h1>
            <p className="text-sm text-white/50">Two-way sync with your eBay seller account via the Sell API.</p>
          </div>
        </div>

        <EbayConnectPanel settings={settings} onConnected={loadSettings} />

        {settings?.connected && (
          <div className="mt-6">
            <EbaySyncPanel settings={settings} onSynced={loadSettings} />
          </div>
        )}

        {settings?.connected && (
          <div className="mt-6">
            <EbaySalesReconciliationPanel />
          </div>
        )}

        {settings?.connected && (
          <div className="mt-6">
            <EbaySageRoutingCard />
          </div>
        )}

        {settings?.connected && (
          <div className="mt-6">
            <EbaySellerOverview settings={settings} />
          </div>
        )}

        <div className="mt-8 text-xs text-white/40 leading-relaxed">
          <p className="font-medium text-white/60 mb-1">Before connecting</p>
          In your eBay Developer Portal, register this app's callback URL as the OAuth redirect for your RuName:
          <code className="block mt-1 bg-black/40 border border-white/10 px-3 py-2 text-white/70">
            https://motion-reality-website.base44.app/functions/ebayAuthCallback
          </code>
          Products need an eBay category set (per product, in the product form's eBay section) to be listed.
        </div>
      </div>
    </div>
  );
}